import { DAYS_IN_WEEK, DEPLOYMENT_KEYS, MINUTES_IN_DAY, MINUTES_IN_HOUR } from 'utils/constants'
import { fromUTCMidnightSeconds, convertMinToDays } from 'utils/time'
import { getAllCycles, getAllVisits } from '../../../../VisitSchedules/routes/VisitSchedulesPage/utils/visitsScheduleUtils'
import moment from 'moment'
import { CYCLE_LABELS, MODULE_FALLBACKS, TIME } from './constants'
import { normalizeSchedules, getScheduleId } from '../../../../Instruments/utils/multiSchedule'

const GRID_DAYS_MAX_RECURRING = 365

// Study always starts at Day 1; never show negative days (e.g. when start date is in the past)
const clampStudyDay = day => (day != null && typeof day === 'number' && day < 1 ? 1 : day)

// Format time_of_day "09:00" / "09:30" for display (e.g. "9am", "9:30am"). GOLD-12267: 12-hour am/pm only.
export const formatTimeOfDay = timeStr => {
  if (!timeStr || typeof timeStr !== 'string') return null
  const trimmed = timeStr.trim()
  if (!trimmed) return null
  const m = moment(trimmed, ['HH:mm', 'H:mm', 'HH:mm:ss'], true)
  if (!m.isValid()) return trimmed
  const h = m.hour()
  const min = m.minute()
  if (min === 0) return h === 12 ? '12pm' : h === 0 ? '12am' : h < 12 ? `${h}am` : `${h - 12}pm`
  return h === 12 ? `12:${min.toString().padStart(2, '0')}pm` : h === 0 ? `12:${min.toString().padStart(2, '0')}am` : h < 12 ? `${h}:${min.toString().padStart(2, '0')}am` : `${h - 12}:${min.toString().padStart(2, '0')}pm`
}

const parseClockTime = timeStr => {
  if (!timeStr || typeof timeStr !== 'string') return null
  const parsed = moment(timeStr.trim(), ['HH:mm', 'H:mm', 'HH:mm:ss'], true)
  return parsed.isValid() ? parsed : null
}

const minutesUntilEndOfDay = timeOfDay => {
  const start = parseClockTime(timeOfDay)
  if (!start) return MINUTES_IN_DAY - 1
  const end = start.clone().hour(23).minute(59)
  return Math.max(1, end.diff(start, 'minutes'))
}

const intervalToMinutes = (interval, intervalType, { subtractStartDay = false, timeOfDay } = {}) => {
  const value = Number(interval)
  if (!Number.isFinite(value) || value < 0) return null
  const type = String(intervalType || 'days').toLowerCase()
  if (type.startsWith('minute')) return value
  if (type.startsWith('hour')) return value * MINUTES_IN_HOUR
  if (type.startsWith('week')) return value * DAYS_IN_WEEK * MINUTES_IN_DAY
  const days = subtractStartDay ? Math.max(0, value - 1) : value
  if (days === 0) return minutesUntilEndOfDay(timeOfDay)
  return days * MINUTES_IN_DAY
}

const resolveExpireStartMoment = (schedule, instrument, options = {}) => {
  const deploy = schedule?.deploy || {}
  if (deploy.absolute != null) {
    try {
      const start = fromUTCMidnightSeconds(deploy.absolute)
      if (start?.isValid()) return start
    } catch (_) {
      /* fall through */
    }
  }
  if (instrument?.deployed) {
    const deployed = moment.utc(instrument.deployed)
    if (deployed.isValid()) return deployed
  }
  if (options.useAbsoluteStartDate && options.absoluteStartDate) {
    const startDate = moment.utc(options.absoluteStartDate)
    if (startDate.isValid()) return startDate
  }
  return null
}

const calendarDaysBetween = (startMoment, endMoment) => {
  if (!startMoment?.isValid() || !endMoment?.isValid()) return null
  return endMoment.clone().startOf('day').diff(startMoment.clone().startOf('day'), 'days')
}

/** GOLD-12268: compact relative expiry, e.g. (+30m), (+6h), (+6d), (+1w 6d), (+Never Expire). */
export const formatRelativeExpirySuffix = totalMinutes => {
  if (totalMinutes == null || !Number.isFinite(totalMinutes)) {
    return `(+${TIME.NEVER_EXPIRE})`
  }
  const minutes = Math.max(0, totalMinutes)
  if (minutes < MINUTES_IN_HOUR) {
    return `(+${Math.max(1, Math.round(minutes))}m)`
  }
  if (minutes < MINUTES_IN_DAY) {
    return `(+${Math.max(1, Math.round(minutes / MINUTES_IN_HOUR))}h)`
  }
  const days = Math.round(minutes / MINUTES_IN_DAY)
  if (days < DAYS_IN_WEEK) {
    return `(+${days}d)`
  }
  const weeks = Math.floor(days / DAYS_IN_WEEK)
  const leftoverDays = days % DAYS_IN_WEEK
  return leftoverDays === 0 ? `(+${weeks}w)` : `(+${weeks}w ${leftoverDays}d)`
}

export const getExpiryDurationMinutes = (schedule, instrument = null, options = {}) => {
  const expire = schedule?.expire || {}
  const deploy = schedule?.deploy || {}

  if (expire.relative_to_send_ideal) {
    return intervalToMinutes(expire.relative_to_send_ideal.interval, expire.relative_to_send_ideal.interval_type, {
      subtractStartDay: true,
      timeOfDay: deploy.time_of_day,
    })
  }
  if (expire.first_login && deploy.first_login) {
    return intervalToMinutes(
      Number(expire.first_login.interval) - Number(deploy.first_login.interval),
      expire.first_login.interval_type || deploy.first_login.interval_type,
      { timeOfDay: deploy.time_of_day },
    )
  }
  if (expire.chain_expire_offset != null && deploy.chain_deploy_offset != null) {
    return intervalToMinutes(Number(expire.chain_expire_offset) - Number(deploy.chain_deploy_offset), 'days', {
      timeOfDay: deploy.time_of_day,
    })
  }
  if (expire.absolute != null) {
    try {
      const endMoment = fromUTCMidnightSeconds(expire.absolute)
      const startMoment = resolveExpireStartMoment(schedule, instrument, options)
      const days = calendarDaysBetween(startMoment, endMoment)
      if (days != null) {
        if (days <= 0) return minutesUntilEndOfDay(deploy.time_of_day)
        return days * MINUTES_IN_DAY
      }
    } catch (_) {
      /* fall through to never-expire */
    }
  }
  if (expire.expire_type && expire.days_offset != null) {
    return intervalToMinutes(expire.days_offset, 'days', { timeOfDay: deploy.time_of_day })
  }

  return null
}

// GOLD-12268: deployment time + relative expiry, e.g. "9am (+6d)" / "9am (+Never Expire)".
export const getInstrumentTimeWindowString = (schedule, instrument = null, options = {}) => {
  const startFormatted = formatTimeOfDay(schedule?.deploy?.time_of_day)
  if (!startFormatted) return null
  return `${startFormatted} ${formatRelativeExpirySuffix(getExpiryDurationMinutes(schedule, instrument, options))}`
}

const getRecurringOccurrenceDays = (
  firstStudyDay,
  schedule,
  useAbsoluteStartDate,
  absoluteStartDate,
  studyEndDay = null,
) => {
  const days = [clampStudyDay(firstStudyDay)]
  const recurring = schedule?.recurring
  if (!recurring || recurring.scheme === 'persistent') return days

  let intervalDays = 1
  if (recurring.scheme === 'n-days' && (recurring.interval == null || recurring.interval < 1)) return days
  if (recurring.scheme === 'n-days') intervalDays = Math.floor(Number(recurring.interval)) || 1
  else if (recurring.scheme === 'n-months') intervalDays = Math.max(1, Math.floor((Number(recurring.interval) || 1) * 30))
  else if (recurring.scheme === 'n-hours') intervalDays = Math.max(1, Math.floor((Number(recurring.interval) || 0) / 24))
  else return days

  const expire = schedule?.expire || {}
  let maxOccurrences = null
  let endStudyDay =
    studyEndDay != null && studyEndDay >= 1
      ? Math.min(GRID_DAYS_MAX_RECURRING, studyEndDay)
      : GRID_DAYS_MAX_RECURRING

  if (recurring.recurrences != null && recurring.recurrences > 0) {
    maxOccurrences = Math.floor(Number(recurring.recurrences))
  } else if (expire.absolute != null && useAbsoluteStartDate && absoluteStartDate) {
    try {
      const startMoment = moment(absoluteStartDate)
      const endMoment = fromUTCMidnightSeconds(expire.absolute)
      if (startMoment.isValid() && endMoment.isValid()) {
        const absoluteEndDay = endMoment.diff(startMoment, 'days') + 1
        if (absoluteEndDay >= 1) endStudyDay = Math.min(endStudyDay, absoluteEndDay)
      }
    } catch (_) {
      /* ignore invalid expire.absolute */
    }
  }

  let day = firstStudyDay
  let count = 1
  while (true) {
    day += intervalDays
    if (maxOccurrences != null && count >= maxOccurrences) break
    if (day > GRID_DAYS_MAX_RECURRING || day > endStudyDay) break
    days.push(clampStudyDay(day))
    count += 1
  }
  return days
}

/** Resolve visit anchor ids from legacy visit_id or multi-visit visit_ids (GOLD-12250). */
export const getChainDeployVisitIds = chainDeploymentInfo => {
  if (!chainDeploymentInfo) return []
  if (Array.isArray(chainDeploymentInfo.visit_ids) && chainDeploymentInfo.visit_ids.length > 0) {
    return chainDeploymentInfo.visit_ids.filter(id => id != null && id !== '')
  }
  if (chainDeploymentInfo.visit_id != null && chainDeploymentInfo.visit_id !== '') {
    return [chainDeploymentInfo.visit_id]
  }
  return []
}

const getVisitStudyDayNumber = visit => {
  if (!visit) return null
  const visitStudyDayRaw =
    visit.metadata?.computed_study_day !== undefined
      ? visit.metadata.computed_study_day
      : visit.day !== undefined && visit.day !== null
        ? visit.day
        : null

  if (visitStudyDayRaw === null || visitStudyDayRaw === undefined) return null

  let visitStudyDayNum = Number(visitStudyDayRaw)
  if (!Number.isFinite(visitStudyDayNum) || visitStudyDayNum < 1) {
    visitStudyDayNum = 1
  }
  return visitStudyDayNum
}

/** Project study day for visit-triggered deploy: anchor visit day + deploy.days_offset. */
export const getVisitChainedStudyDay = (schedule, visits = [], selectedArmVisitIds = null) => {
  const chainDeployment = schedule?.chain_deployment_info
  const visitIds = getChainDeployVisitIds(chainDeployment)
  if (!visitIds.length) return null

  const isVisitChained =
    chainDeployment &&
    (DEPLOYMENT_KEYS.chainDeployByVisit in chainDeployment || visitIds.length > 0)
  if (!isVisitChained) return null

  const candidateIds =
    selectedArmVisitIds != null
      ? visitIds.filter(id => selectedArmVisitIds.has(String(id)))
      : visitIds

  if (selectedArmVisitIds != null && candidateIds.length === 0) {
    return null
  }

  const idsToTry = candidateIds.length ? candidateIds : visitIds
  const daysOffsetRaw = schedule?.deploy?.days_offset
  const daysOffsetNum =
    daysOffsetRaw !== undefined && daysOffsetRaw !== null && daysOffsetRaw !== ''
      ? Number(daysOffsetRaw)
      : 0
  const offset = Number.isFinite(daysOffsetNum) ? daysOffsetNum : 0

  for (const visitId of idsToTry) {
    const visit = visits.find(v => v.id != null && String(v.id) === String(visitId))
    if (!visit) continue
    const visitStudyDayNum = getVisitStudyDayNumber(visit)
    if (visitStudyDayNum == null) continue
    const actualOccurrenceDay = visitStudyDayNum + offset
    return clampStudyDay(actualOccurrenceDay) ?? actualOccurrenceDay
  }

  return null
}

const getInstrumentChainTriggerIds = schedule => {
  const chainDeployment = schedule?.chain_deployment_info
  if (!chainDeployment || DEPLOYMENT_KEYS.chainDeployByVisit in chainDeployment) return []

  const triggerIds = chainDeployment.prev_instrument_id
  if (Array.isArray(triggerIds)) {
    return triggerIds.filter(id => id != null && id !== '')
  }
  return triggerIds != null && triggerIds !== '' ? [triggerIds] : []
}

/**
 * Project an instrument-triggered deploy from the projected days of its trigger instruments.
 * OR chains use the first trigger; AND chains wait for the last trigger.
 */
export const getInstrumentChainedStudyDay = (schedule, parentStudyDays = new Map()) => {
  const triggerIds = getInstrumentChainTriggerIds(schedule)
  if (!triggerIds.length) return null

  const triggerDays = triggerIds.map(id => parentStudyDays.get(String(id)))
  if (triggerDays.some(day => day == null || !Number.isFinite(Number(day)))) return null

  const normalizedDays = triggerDays.map(Number)
  const operator = schedule?.chain_deployment_info?.deployment_condition?.operator
  const triggerDay = operator === 'OR' ? Math.min(...normalizedDays) : Math.max(...normalizedDays)
  const offsetValue = Number(schedule?.deploy?.chain_deploy_offset)
  const offset = Number.isFinite(offsetValue) ? offsetValue : 0
  const projectedDay = triggerDay + offset
  return clampStudyDay(projectedDay) ?? projectedDay
}

/**
 * Derive scheduled timeline items and cycle ranges from arms, instruments, and study config.
 * Client-side projection only — does not execute schedules.
 */
export const deriveScheduledItemsFromArms = (
  arms,
  selectedArmId,
  instruments = [],
  useAbsoluteStartDate = false,
  absoluteStartDate = null,
  includeVisitCycle = true,
  studyDurationDays = null,
) => {
  const items = []
  const cycleRanges = []

  const processInstruments = (visits = [], studyEndDay = null, selectedArmVisitIds = null) => {
    const instrumentsById = new Map(
      instruments
        .filter(instrument => instrument?.id != null)
        .map(instrument => [String(instrument.id), instrument]),
    )
    const instrumentStudyDays = new Map()
    const resolvingInstrumentIds = new Set()

    const resolveNonInstrumentChainedStudyDay = (instrument, schedule) => {
      const chainDeployment = schedule?.chain_deployment_info
      const isVisitChainedDeploy =
        !!chainDeployment &&
        (DEPLOYMENT_KEYS.chainDeployByVisit in chainDeployment ||
          getChainDeployVisitIds(chainDeployment).length > 0)

      if (isVisitChainedDeploy) {
        return getVisitChainedStudyDay(schedule, visits, selectedArmVisitIds)
      }

      if (useAbsoluteStartDate && absoluteStartDate && !schedule?.deploy?.first_login) {
        const startDate = moment.utc(absoluteStartDate).startOf('day')
        if (schedule?.deploy?.absolute) {
          const epochSeconds = Number(schedule.deploy.absolute)
          const absoluteDate = Number.isFinite(epochSeconds)
            ? moment.utc(epochSeconds * 1000).startOf('day')
            : null
          if (absoluteDate?.isValid() && startDate.isValid()) {
            return Math.max(1, absoluteDate.diff(startDate, 'days') + 1)
          }
        } else if (instrument.deployed) {
          const deployedDate = moment.utc(instrument.deployed).startOf('day')
          if (deployedDate.isValid() && startDate.isValid()) {
            return Math.max(1, deployedDate.diff(startDate, 'days') + 1)
          }
        }
      }

      const firstLogin = schedule?.deploy?.first_login
      if (firstLogin && firstLogin.interval != null) {
        const interval = Number(firstLogin.interval)
        const projectedDay = Number.isFinite(interval) ? 1 + interval : 1
        return clampStudyDay(projectedDay) ?? projectedDay
      }

      if (schedule?.deploy?.days_offset != null && schedule.deploy.days_offset !== '') {
        const offset = Number(schedule.deploy.days_offset)
        const projectedDay = Number.isFinite(offset) ? offset : schedule.deploy.days_offset
        return clampStudyDay(projectedDay) ?? projectedDay
      }

      if (schedule?.deploy?.now !== undefined) {
        if (instrument.deployed && useAbsoluteStartDate && absoluteStartDate) {
          const startDate = moment.utc(absoluteStartDate).startOf('day')
          const deployedDate = moment.utc(instrument.deployed).startOf('day')
          if (deployedDate.isValid() && startDate.isValid()) {
            return Math.max(1, deployedDate.diff(startDate, 'days') + 1)
          }
        }
        const visitDays = visits.map(getVisitStudyDayNumber).filter(day => day != null)
        return visitDays.length ? Math.min(...visitDays) : 1
      }

      if (schedule?.deploy?.absolute != null && (!useAbsoluteStartDate || !absoluteStartDate)) {
        return 1
      }
      return null
    }

    const resolveInstrumentStudyDay = instrumentId => {
      const normalizedId = String(instrumentId)
      if (instrumentStudyDays.has(normalizedId)) return instrumentStudyDays.get(normalizedId)
      if (resolvingInstrumentIds.has(normalizedId)) return null

      const instrument = instrumentsById.get(normalizedId)
      if (!instrument) return null

      resolvingInstrumentIds.add(normalizedId)
      const projectedDays = normalizeSchedules(instrument.metadata?.schedule)
        .map(schedule => {
          const triggerIds = getInstrumentChainTriggerIds(schedule)
          if (!triggerIds.length) return resolveNonInstrumentChainedStudyDay(instrument, schedule)

          const parentStudyDays = new Map()
          triggerIds.forEach(triggerId => {
            const parentDay = resolveInstrumentStudyDay(triggerId)
            if (parentDay != null) parentStudyDays.set(String(triggerId), parentDay)
          })
          return getInstrumentChainedStudyDay(schedule, parentStudyDays)
        })
        .filter(day => day != null && Number.isFinite(Number(day)))
        .map(Number)

      resolvingInstrumentIds.delete(normalizedId)
      const projectedDay = projectedDays.length ? Math.min(...projectedDays) : null
      instrumentStudyDays.set(normalizedId, projectedDay)
      return projectedDay
    }

    instruments.forEach(instrument => {
      const schedules = normalizeSchedules(instrument.metadata?.schedule)
      if (!schedules.length) return

      schedules.forEach((schedule, scheduleIndex) => {
        let studyDay = null
        const chainDeployment = schedule?.chain_deployment_info
        const isVisitChainedDeploy =
          !!chainDeployment &&
          (DEPLOYMENT_KEYS.chainDeployByVisit in chainDeployment ||
            getChainDeployVisitIds(chainDeployment).length > 0)
        const instrumentTriggerIds = getInstrumentChainTriggerIds(schedule)
        const isInstrumentChainedDeploy = instrumentTriggerIds.length > 0

        if (isVisitChainedDeploy) {
          studyDay = getVisitChainedStudyDay(schedule, visits, selectedArmVisitIds)
          if (studyDay === null) {
            return
          }
        }

        if (isInstrumentChainedDeploy) {
          const parentStudyDays = new Map()
          instrumentTriggerIds.forEach(triggerId => {
            const parentDay = resolveInstrumentStudyDay(triggerId)
            if (parentDay != null) parentStudyDays.set(String(triggerId), parentDay)
          })
          studyDay = getInstrumentChainedStudyDay(schedule, parentStudyDays)
          if (studyDay === null) {
            return
          }
        }

        if (
          studyDay === null &&
          !isInstrumentChainedDeploy &&
          useAbsoluteStartDate &&
          absoluteStartDate &&
          !schedule?.deploy?.first_login
        ) {
          const startDate = moment.utc(absoluteStartDate).startOf('day')
          if (schedule?.deploy?.absolute) {
            const epochSeconds = Number(schedule.deploy.absolute)
            const absoluteDate = Number.isFinite(epochSeconds)
              ? moment.utc(epochSeconds * 1000).startOf('day')
              : null
            if (absoluteDate?.isValid() && startDate.isValid()) {
              const raw = absoluteDate.diff(startDate, 'days') + 1
              studyDay = Math.max(1, raw)
            }
          } else if (instrument.deployed) {
            const deployedDate = moment.utc(instrument.deployed).startOf('day')
            if (deployedDate.isValid() && startDate.isValid()) {
              const raw = deployedDate.diff(startDate, 'days') + 1
              studyDay = Math.max(1, raw)
            }
          }
        }

        if (studyDay === null && schedule) {
          const fl = schedule.deploy?.first_login
          if (fl && fl.interval !== undefined && fl.interval !== null) {
            const intervalNum = Number(fl.interval)
            const firstStudyDay = Number.isFinite(intervalNum) ? 1 + intervalNum : 1
            studyDay = clampStudyDay(firstStudyDay) ?? firstStudyDay
          } else if (
            !isVisitChainedDeploy &&
            !isInstrumentChainedDeploy &&
            schedule.deploy?.days_offset !== undefined &&
            schedule.deploy.days_offset !== null &&
            schedule.deploy.days_offset !== ''
          ) {
            const raw = schedule.deploy.days_offset
            studyDay = clampStudyDay(raw) ?? raw
          } else if (schedule.deploy?.now !== undefined) {
            if (instrument.deployed && useAbsoluteStartDate && absoluteStartDate) {
              const startDate = moment.utc(absoluteStartDate).startOf('day')
              const deployedDate = moment.utc(instrument.deployed).startOf('day')
              if (deployedDate.isValid() && startDate.isValid()) {
                const raw = deployedDate.diff(startDate, 'days') + 1
                studyDay = Math.max(1, raw)
              }
            }
            if (studyDay === null && visits.length > 0) {
              const earliestVisit = visits.reduce((earliest, visit) => {
                const visitDay =
                  visit.metadata?.computed_study_day !== undefined
                    ? visit.metadata.computed_study_day
                    : visit.day !== undefined && visit.day !== null
                      ? visit.day
                      : null
                const earliestDay =
                  earliest.metadata?.computed_study_day !== undefined
                    ? earliest.metadata.computed_study_day
                    : earliest.day !== undefined && earliest.day !== null
                      ? earliest.day
                      : null

                if (visitDay !== null && visitDay !== undefined) {
                  if (earliestDay === null || earliestDay === undefined || visitDay < earliestDay) {
                    return visit
                  }
                }
                return earliest
              }, visits[0])

              const earliestDay =
                earliestVisit.metadata?.computed_study_day !== undefined
                  ? earliestVisit.metadata.computed_study_day
                  : earliestVisit.day !== undefined && earliestVisit.day !== null
                    ? earliestVisit.day
                    : 1

              studyDay = earliestDay > 0 ? earliestDay : 1
            }
            if (studyDay === null) {
              studyDay = 1
            }
          } else if (schedule.deploy?.absolute !== undefined && schedule.deploy.absolute !== null) {
            if (!useAbsoluteStartDate || !absoluteStartDate) {
              studyDay = 1
            }
          }
        }

        if (studyDay !== null && studyDay !== undefined) {
          let occurrenceDays = getRecurringOccurrenceDays(
            studyDay,
            schedule,
            useAbsoluteStartDate,
            absoluteStartDate,
            studyEndDay,
          )
          const relExpire = schedule?.expire?.relative_to_send_ideal
          if (relExpire && (relExpire.interval == null || relExpire.interval >= 1)) {
            const interval = Math.floor(Number(relExpire.interval)) || 1
            const visibilityDays = new Set(occurrenceDays)
            occurrenceDays.forEach(occDay => {
              for (let d = 1; d <= interval; d++) {
                const extraDay = occDay + d
                if (extraDay <= GRID_DAYS_MAX_RECURRING && (studyEndDay == null || extraDay <= studyEndDay)) {
                  visibilityDays.add(extraDay)
                }
              }
            })
            occurrenceDays = Array.from(visibilityDays).sort((a, b) => a - b)
          }
          if (
            schedule?.deploy?.absolute != null &&
            schedule?.expire?.absolute != null &&
            useAbsoluteStartDate &&
            absoluteStartDate
          ) {
            const startDate = moment.utc(absoluteStartDate).startOf('day')
            const expireEpoch = Number(schedule.expire.absolute)
            const expireDate = Number.isFinite(expireEpoch)
              ? moment.utc(expireEpoch * 1000).startOf('day')
              : null
            if (expireDate?.isValid() && startDate.isValid()) {
              const expireStudyDay = expireDate.diff(startDate, 'days') + 1
              if (expireStudyDay >= studyDay) {
                const visibilityDays = new Set(occurrenceDays)
                for (let d = studyDay; d <= expireStudyDay; d++) {
                  if (d >= 1 && d <= GRID_DAYS_MAX_RECURRING) visibilityDays.add(d)
                }
                occurrenceDays = Array.from(visibilityDays).sort((a, b) => a - b)
              }
            }
          }
          occurrenceDays.forEach((day, index) => {
            const clampedDay = clampStudyDay(day) ?? day
            const timeWindow =
              getInstrumentTimeWindowString(schedule, instrument, {
                useAbsoluteStartDate,
                absoluteStartDate,
              }) || formatTimeOfDay(schedule?.deploy?.time_of_day)
            items.push({
              id: `instrument-${instrument.id}-sched-${scheduleIndex}-occ-${index}`,
              studyDay: clampedDay,
              label:
                instrument.metadata?.display_name ||
                instrument.title ||
                instrument.name ||
                MODULE_FALLBACKS.INSTRUMENT_LABEL,
              type: 'instrument',
              window: timeWindow,
              instrumentId: instrument.id,
              instrumentData: instrument,
              scheduleIndex,
              scheduleId: getScheduleId(schedule),
            })
          })
        }
      })
    })
  }

  const recurringCap =
    studyDurationDays != null && studyDurationDays >= 1 ? Math.floor(Number(studyDurationDays)) : null

  if (!arms || arms.length === 0) {
    processInstruments([], recurringCap)
    return {
      items: items.sort((a, b) => a.studyDay - b.studyDay),
      cycleRanges: [],
    }
  }

  if (!selectedArmId) {
    const allVisits = getAllVisits(arms) || []
    processInstruments(allVisits, recurringCap)
    return {
      items: items.sort((a, b) => a.studyDay - b.studyDay),
      cycleRanges: [],
    }
  }

  const selectedArmIdStr = selectedArmId != null ? String(selectedArmId) : ''
  const selectedArm =
    arms.find(arm => arm != null && arm.id != null && String(arm.id) === selectedArmIdStr) || arms[0]
  if (!selectedArm) {
    const allVisits = getAllVisits(arms) || []
    processInstruments(allVisits, recurringCap)
    return {
      items: items.sort((a, b) => a.studyDay - b.studyDay),
      cycleRanges: [],
    }
  }

  const cycles = (getAllCycles([selectedArm]) || []).filter(
    cycle => !cycle.is_dummy && !cycle.metadata?.is_dummy,
  )
  const visits = getAllVisits([selectedArm]) || []
  const allVisits = getAllVisits(arms) || []

  const visitToCycleMap = new Map()
  cycles.forEach(cycle => {
    if (cycle.visits && Array.isArray(cycle.visits)) {
      cycle.visits.forEach(visit => {
        if (visit.id) {
          visitToCycleMap.set(visit.id, cycle)
        }
      })
    }
  })

  if (includeVisitCycle) {
    const rawRanges = []
    cycles.forEach(cycle => {
      const projectedStartDay = cycle.metadata?.projected_study_day
      const projectedEndDay = cycle.metadata?.projected_study_end_day
      const lengthValueMinutes = cycle.length_value != null ? Number(cycle.length_value) : null

      if (
        projectedStartDay !== undefined &&
        projectedStartDay !== null &&
        projectedEndDay !== undefined &&
        projectedEndDay !== null
      ) {
        const startDay = Math.max(1, projectedStartDay)
        let endDay
        if (lengthValueMinutes != null && lengthValueMinutes >= 0 && Number.isFinite(lengthValueMinutes)) {
          const durationDays = Math.max(1, Math.floor(convertMinToDays(lengthValueMinutes, false)))
          endDay = startDay + durationDays - 1
        } else {
          const lastDayInclusive = Math.max(1, Number(projectedEndDay)) - 1
          endDay = Math.max(startDay, lastDayInclusive)
        }
        rawRanges.push({
          cycleId: cycle.id,
          cycleName: cycle.name,
          startDay,
          endDay,
          cycleData: cycle,
        })
      }
    })

    rawRanges.sort((a, b) => a.startDay - b.startDay)
    const adjustedRanges = []
    for (let i = 0; i < rawRanges.length; i++) {
      const prev = i > 0 ? adjustedRanges[i - 1] : null
      let startDay = rawRanges[i].startDay
      let endDay = rawRanges[i].endDay
      if (prev != null && startDay <= prev.endDay) {
        const delta = prev.endDay + 1 - startDay
        startDay = prev.endDay + 1
        endDay = endDay + delta
      }
      const range = {
        ...rawRanges[i],
        startDay,
        endDay,
      }
      adjustedRanges.push(range)
      cycleRanges.push(range)
      const cycle = range.cycleData
      items.push({
        id: `cycle-start-${cycle.id}`,
        studyDay: startDay,
        label: `${CYCLE_LABELS.START_PREFIX}${cycle.name}`,
        type: 'cycle',
        cycleName: cycle.name || MODULE_FALLBACKS.CYCLE_NAME_NA,
        window: null,
        cycleData: cycle,
      })
      items.push({
        id: `cycle-end-${cycle.id}`,
        studyDay: endDay,
        label: `${CYCLE_LABELS.END_PREFIX}${cycle.name}`,
        type: 'cycle',
        cycleName: cycle.name || MODULE_FALLBACKS.CYCLE_NAME_NA,
        window: null,
        cycleData: cycle,
      })
    }
  }

  visits.forEach(visit => {
    const raw =
      visit.metadata?.computed_study_day !== undefined
        ? visit.metadata.computed_study_day
        : visit.day !== undefined && visit.day !== null
          ? visit.day
          : null

    if (raw !== null && raw !== undefined) {
      const studyDay = clampStudyDay(raw) ?? raw
      const cycle = includeVisitCycle ? visitToCycleMap.get(visit.id) : null
      items.push({
        id: `visit-${visit.id}`,
        studyDay,
        label: visit.name || visit.metadata?.display_name || MODULE_FALLBACKS.VISIT_LABEL,
        type: 'visit',
        cycleName: includeVisitCycle ? cycle?.name || MODULE_FALLBACKS.CYCLE_NAME_NA : null,
        window: visit.window !== undefined && visit.window !== null ? `±${visit.window}` : null,
        visitId: visit.id,
        visitData: visit,
        cycleId: includeVisitCycle ? cycle?.id : null,
      })
    }
  })

  let studyEndDay = null
  if (studyDurationDays != null && studyDurationDays >= 1) {
    studyEndDay = Math.floor(Number(studyDurationDays))
  }
  if (studyEndDay == null || studyEndDay < 1) {
    studyEndDay = 0
    cycleRanges.forEach(r => {
      if (r.endDay != null && r.endDay >= 1) studyEndDay = Math.max(studyEndDay, r.endDay)
    })
    if (studyEndDay < 1) studyEndDay = null
  }

  const selectedArmVisitIds = new Set(visits.map(v => (v.id != null ? String(v.id) : null)).filter(Boolean))
  processInstruments(allVisits, studyEndDay, selectedArmVisitIds)

  return {
    items: items.sort((a, b) => a.studyDay - b.studyDay),
    cycleRanges: cycleRanges.sort((a, b) => a.startDay - b.startDay),
  }
}
